'use client';

import React, { useEffect, useState } from 'react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { cx } from '@/utils';
import { MdSignalWifiOff, MdSignalWifiStatusbarConnectedNoInternet4, MdWifi } from 'react-icons/md';

interface NetworkStatusBannerProps {
  /** Mostrar el banner incluso cuando la conexión es lenta */
  showSlowConnection?: boolean;
  /** Clase CSS adicional */
  className?: string;
  /** Posición del banner */
  position?: 'top' | 'bottom';
  /** Z-index del banner */
  zIndex?: number;
}

/**
 * Componente que muestra un banner cuando hay problemas de conexión a internet.
 * Se muestra automáticamente cuando se detecta que el usuario está offline
 * o tiene una conexión lenta.
 *
 * @example
 * // En _app.tsx o layout principal
 * <NetworkStatusBanner />
 */
export const NetworkStatusBanner: React.FC<NetworkStatusBannerProps> = ({
  showSlowConnection = true,
  className,
  position = 'top',
  zIndex = 9999,
}) => {
  const { isOffline, isSlow, effectiveType } = useNetworkStatus();
  const [isVisible, setIsVisible] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  // Usar ref para trackear estado previo sin causar re-renders
  const wasOfflineRef = React.useRef(false);
  const reconnectedTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Maneja la visibilidad con animación
  useEffect(() => {
    // Limpiar timer anterior si existe
    if (reconnectedTimerRef.current) {
      clearTimeout(reconnectedTimerRef.current);
      reconnectedTimerRef.current = null;
    }

    if (isOffline) {
      setIsVisible(true);
      setShowReconnected(false);
      wasOfflineRef.current = true;
    } else if (wasOfflineRef.current) {
      // Usuario se reconectó
      wasOfflineRef.current = false;
      setShowReconnected(true);
      setIsVisible(true);

      // Ocultar el mensaje de reconexión después de 3 segundos
      reconnectedTimerRef.current = setTimeout(() => {
        setShowReconnected(false);
        setIsVisible(false);
      }, 3000);
    } else if (showSlowConnection && isSlow) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    return () => {
      if (reconnectedTimerRef.current) {
        clearTimeout(reconnectedTimerRef.current);
      }
    };
  }, [isOffline, isSlow, showSlowConnection]);

  // No renderizar si no hay problemas de conexión
  if (!isVisible && !showReconnected) {
    return null;
  }

  const getContent = () => {
    if (showReconnected) {
      return {
        icon: <MdWifi className="size-5 animate-pulse" />,
        title: '¡Conexión restaurada!',
        message: 'Tu conexión a internet se ha restablecido.',
        bgColor: 'bg-green',
        borderColor: 'border-greenDark',
        textColor: 'text-white',
      };
    }

    if (isOffline) {
      return {
        icon: <MdSignalWifiOff className="size-5 animate-pulse" />,
        title: 'Sin conexión a internet',
        message:
          'Parece que no tienes conexión a internet. Algunas funciones pueden no estar disponibles hasta que se restablezca la conexión.',
        bgColor: 'bg-red-600',
        borderColor: 'border-red-700',
        textColor: 'text-white',
      };
    }

    if (isSlow) {
      return {
        icon: <MdSignalWifiStatusbarConnectedNoInternet4 className="size-5" />,
        title: 'Conexión lenta detectada',
        message: `Tu conexión parece ser lenta (${effectiveType}). Algunas funciones pueden tardar más de lo esperado.`,
        bgColor: 'bg-yellow',
        borderColor: 'border-gold2',
        textColor: 'text-black',
      };
    }

    return null;
  };

  const content = getContent();

  if (!content) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cx(
        'fixed left-1/2 w-[calc(100%-1rem)] max-w-3xl -translate-x-1/2 rounded-md px-4 py-3 shadow-lg transition-all duration-300 ease-in-out',
        content.bgColor,
        content.textColor,
        `border ${content.borderColor}`,
        position === 'top' ? 'top-2' : 'bottom-2',
        isVisible || showReconnected ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
        className
      )}
      style={{ zIndex }}
    >
      <div className="flex items-center justify-center gap-3">
        <div className="flex-shrink-0">{content.icon}</div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
          <span className="font-semibold">{content.title}</span>
          <span className="text-sm opacity-90">{content.message}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente más compacto que solo muestra un indicador pequeño
 * en la esquina de la pantalla cuando hay problemas de conexión.
 */
export const NetworkStatusIndicator: React.FC<{
  className?: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}> = ({ className, position = 'bottom-right' }) => {
  const { isOffline, isSlow } = useNetworkStatus();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(isOffline || isSlow);
  }, [isOffline, isSlow]);

  if (!isVisible) return null;

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  return (
    <div
      role="status"
      aria-label={isOffline ? 'Sin conexión a internet' : 'Conexión lenta'}
      className={cx(
        'fixed z-50 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg transition-all duration-300',
        isOffline ? 'bg-red-600 text-white' : 'bg-yellow text-black',
        positionClasses[position],
        isVisible ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
        className
      )}
    >
      {isOffline ? (
        <MdSignalWifiOff className="size-5 animate-pulse" />
      ) : (
        <MdSignalWifiStatusbarConnectedNoInternet4 className="size-5" />
      )}
      <span className="text-sm font-medium">{isOffline ? 'Sin conexión' : 'Conexión lenta'}</span>
    </div>
  );
};

export default NetworkStatusBanner;
